import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import IcArrowRightFillBase from 'src/assets/images/icArrowRightFill.svg';
import ExternalLinkButton from 'src/components/ExternalLink/ExternalLinkButton';
import withWebviewValidation from 'src/components/HOC/withWebviewValidation';
import LoadingIndicator from 'src/components/LoadingIndicator';
import useIsomorphicLayoutEffect from 'src/hooks/useIsomorphicLayoutEffect';
import { useAzarToCheeroGuideQuery } from 'src/hooks/usePopupQuery';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';
import { safeWindow } from 'src/utils/static-generation';
const Root = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  padding-top: var(--sat);
  background-color: ${({
  theme
}) => theme.color.bg1};
`;
const ButtonWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: calc(92px + var(--sab));
  background-color: ${({
  theme
}) => theme.color.bg1};
  padding: 16px 24px calc(24px + var(--sab)) 24px;
`;
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  height: calc(100% - var(--sat) - var(--sab));
  width: 100%;
  padding: 24px 24px calc(92px + var(--sab)) 24px;
  color: ${({
  theme
}) => theme.color.contentDeep};
  overflow-y: auto;
`;
const ContentTitle = styled.p`
  margin-bottom: 32px;
  text-align: center;
  font-size: 24px;
  font-weight: 900;
  line-height: 31px;
  color: ${({
  theme
}) => theme.color.contentDeep};
`;
const ContentImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  width: 100%;
  margin-bottom: 32px;
`;
const ArrowRightIcon = styled(IcArrowRightFillBase)`
  width: 24px;
  height: 24px;
  color: ${({
  theme
}) => theme.color.content7};
  ${({
  theme
}) => theme.isRtl && `
    transform: scale(-1);
  `}
`;
const SymbolIcon = styled.img`
  width: 72px;
  height: 72px;
`;
const ContentDescription = styled.p`
  text-align: center;
  font-size: 16px;
  line-height: 21px;
  color: ${({
  theme
}) => theme.color.content11};
  white-space: pre-wrap;
`;
type CountryCode = 'TR' | 'SA';

// Azar to Cheero 로그인 블록 팝업을 지원 국가 목록
const SUPPORTED_COUNTRY_CODES: CountryCode[] = ['TR', 'SA'];
const DEFAULT_COUNTRY_CODE = 'TR';

// 구/신버전을 구분하는 최소 API 버전
const MIN_API_VERSION = 2645;
const cheeroAppDownloadAppsflyerUrlMap = {
  TR: {
    IOS: 'https://cheeroapp.onelink.me/wlk8/tc7zcvql',
    ANDROID: {
      latest: 'https://cheeroapp.onelink.me/wlk8/56x248ta',
      old: 'https://cheeroapp.onelink.me/wlk8/hrbl0kz9'
    }
  },
  SA: {
    IOS: 'https://cheeroapp.onelink.me/wlk8/63scsgzl',
    ANDROID: {
      latest: 'https://cheeroapp.onelink.me/wlk8/4aur7djs',
      old: 'https://cheeroapp.onelink.me/wlk8/v9bcieyx'
    }
  }
} as const;
const isLatestApiVersion = (apiVersion: number) => apiVersion >= MIN_API_VERSION;
const isValidCountyCode = (countryCode: string): countryCode is CountryCode => (SUPPORTED_COUNTRY_CODES as string[]).includes(countryCode);
const CheeroRedirectView = () => {
  const {
    isReady,
    query
  } = useRouter();
  const countryCode = `${query.countryCode ?? ''}`;
  const safeCountryCode = useMemo(() => isValidCountyCode(countryCode) ? countryCode : DEFAULT_COUNTRY_CODE, [countryCode]);
  const [layoutType, setLayoutType] = useState<'full' | 'small'>('full'); // full: 전체 화면, small: 줄어든 화면

  const {
    data
  } = useAzarToCheeroGuideQuery(safeCountryCode);
  const azarToCheeroGuide = data?.data?.result;

  /**
   * 버전 분기에 따른 웹뷰 노출
   * - iOS 2645 이상은 풀화면 웹뷰 노출 이하버전에서는 iOS네이티브 팝업 노출
   * - Android 2645 이상은 풀화면 웹뷰 노출 이하버전에서는 작은 화면의 안드로이드 팝업 노출
   *
   * appsflyer link 분기 추가
   * - 앱 다운로드 페이지로의 이동은 이벤트 로그로 확인 가능하지만, 실제 설치로 이어지는 지표는 appsflyer 통해 확인해야 함.
   * - 현재 안드로이드 구버전에서의 작은 팝업을 통해 유입되는 사용자를 구문하기 힘든 점을 해결하기 위해, 새로운 appsflyer 링크를 생성, 이를 android 구버전의 작은 팝업에 사용하여 appsflyer에서 유입 경로를 구분할 수 있도록 함.
   */
  const cheeroAppDownloadUrl = useMemo(() => {
    const platform = safeWindow?.AzarJs?.getPlatform?.();
    const apiVersion = safeWindow?.AzarJs?.getApiVersion?.();
    const urlsForCountry = cheeroAppDownloadAppsflyerUrlMap[safeCountryCode];
    if (platform === 'ANDROID') {
      return isLatestApiVersion(Number(apiVersion || 0)) ? urlsForCountry.ANDROID.latest : urlsForCountry.ANDROID.old;
    }
    return urlsForCountry.IOS;
  }, [safeCountryCode]);
  useEffect(() => {
    if (isReady) {
      pushEvent({
        eventType: EVENT_TYPE.DEBUG,
        eventName: EVENT_NAME.LOGIN__IMPRESSION_CHEERO_REDIRECT_POPUP,
        eventParams: {
          country_code: countryCode,
          search: safeWindow?.location?.search,
          release: process.env.NEXT_PUBLIC_RELEASE
        }
      });
      pushEvent({
        eventType: EVENT_TYPE.POPUP,
        eventName: EVENT_NAME.LOGIN__IMPRESSION_CHEERO_REDIRECT_POPUP,
        eventParams: {
          tab: 'login',
          page: 'main',
          action_category: 'impression',
          target: 'cheero_redirect_popup',
          country_code: countryCode
        }
      });
    }
  }, [isReady, countryCode]);
  useIsomorphicLayoutEffect(() => {
    const checkHeight = () => {
      setLayoutType(window.innerHeight > 500 ? 'full' : 'small');
    };
    checkHeight();
    window.addEventListener('resize', checkHeight);
    return () => {
      window.removeEventListener('resize', checkHeight);
    };
  }, []);
  const handleClickButton = useCallback(() => {
    pushEvent({
      eventType: EVENT_TYPE.DEBUG,
      eventName: EVENT_NAME.LOGIN__CLICK_CHEERO_REDIRECT_POPUP,
      eventParams: {
        isReady,
        country_code: countryCode,
        search: safeWindow?.location?.search,
        release: process.env.NEXT_PUBLIC_RELEASE
      }
    });
    pushEvent({
      eventType: EVENT_TYPE.POPUP,
      eventName: EVENT_NAME.LOGIN__CLICK_CHEERO_REDIRECT_POPUP,
      eventParams: {
        tab: 'login',
        page: 'main',
        action_category: 'click',
        target: 'cheero_redirect_popup',
        target_detail: 'accept',
        country_code: countryCode
      }
    });
  }, [isReady, countryCode]);
  if (!countryCode || !azarToCheeroGuide) {
    return <Root>
        <LoadingIndicator size={30} />
      </Root>;
  }
  return <Root data-sentry-element="Root" data-sentry-component="CheeroRedirectView" data-sentry-source-file="CheeroRedirect.tsx">
      <ContentWrapper data-sentry-element="ContentWrapper" data-sentry-source-file="CheeroRedirect.tsx">
        <ContentTitle data-sentry-element="ContentTitle" data-sentry-source-file="CheeroRedirect.tsx">{azarToCheeroGuide.title}</ContentTitle>
        {layoutType === 'full' && <ContentImageWrapper>
            {/* SA에서는 Azar와 Cheero의 연결성을 언급하지 않기 위해 Azar 로고를 보여주지 않는다. (https://hpcnt.slack.com/archives/C089TUY1Q93/p1764066851740459?thread_ts=1764066559.635979&cid=C089TUY1Q93) */}
            {safeCountryCode !== 'SA' && <>
                <SymbolIcon src='/images/modal/cheeroRedirect/imgAzarSymbol.svg' alt='azar symbol' />
                <ArrowRightIcon />
              </>}
            <SymbolIcon src='/images/modal/cheeroRedirect/imgCheeroSymbol.svg' alt='cheero symbol' />
          </ContentImageWrapper>}
        <ContentDescription data-sentry-element="ContentDescription" data-sentry-source-file="CheeroRedirect.tsx">
          {layoutType === 'small' ? azarToCheeroGuide.shortBody : azarToCheeroGuide.body}
        </ContentDescription>
      </ContentWrapper>
      <ButtonWrapper data-sentry-element="ButtonWrapper" data-sentry-source-file="CheeroRedirect.tsx">
        <ExternalLinkButton href={cheeroAppDownloadUrl} onClick={handleClickButton} id='cheero-download-button' color='primary' variant='contained' target='_self' data-sentry-element="ExternalLinkButton" data-sentry-source-file="CheeroRedirect.tsx">
          {azarToCheeroGuide.buttonText}
        </ExternalLinkButton>
      </ButtonWrapper>
    </Root>;
};
export default withWebviewValidation(CheeroRedirectView);